import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import api from "@/lib/api";

export default function WaitlistModal({ city, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", contact_method: "email" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.contact_method === "email" && !form.email) return toast.error("Email required.");
    if (form.contact_method === "sms" && !form.phone) return toast.error("Phone required.");
    setSaving(true);
    try {
      await api.post("/waitlist", { city_id: city.city_id, ...form });
      toast.success("You're on the list.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="waitlist-modal" className="fixed inset-0 z-[55] bg-[#050507]/95 backdrop-blur-xl flex items-center justify-center p-6">
      <div className="max-w-md w-full glass hairline p-8 fade-up relative">
        <button data-testid="waitlist-close" onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="eyebrow eyebrow-blue">{city.request_only ? "Request Availability" : "Join Waitlist"}</div>
        <h2 className="mt-3 h-editorial text-3xl">{city.name}</h2>
        {city.dates && <div className="mt-2 text-xs text-zinc-500 font-mono-eyebrow uppercase tracking-[0.24em]">{city.dates}{city.timezone ? ` · ${city.timezone.replace("_", " ")}` : ""}</div>}
        <p className="mt-3 text-sm text-zinc-400">{city.request_only ? "Exact session hours are being finalized. Add yourself and I'll reach out privately when they open." : "First 20 sign-ups get notified the second we open the city. The rest 12 hours later."}</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="eyebrow eyebrow-blue block mb-2">Alias (optional)</label>
            <input data-testid="waitlist-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3" />
          </div>
          <div>
            <label className="eyebrow eyebrow-blue block mb-2">How should we reach you?</label>
            <div className="grid grid-cols-2 gap-2">
              {["email", "sms"].map(m => (
                <button key={m} type="button" data-testid={`waitlist-method-${m}`} onClick={() => setForm({...form, contact_method: m})}
                  className={`px-4 py-2.5 text-xs font-mono-eyebrow uppercase tracking-[0.18em] rounded-full border transition-all ${form.contact_method === m ? "bg-[#0052FF] border-[#0052FF] text-white" : "border-white/15 text-zinc-400 hover:border-[#00D1FF]/50"}`}>
                  {m === "email" ? "Email" : "Text (SMS)"}
                </button>
              ))}
            </div>
          </div>
          {form.contact_method === "email" ? (
            <div>
              <label className="eyebrow eyebrow-blue block mb-2">Email</label>
              <input data-testid="waitlist-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3" required />
            </div>
          ) : (
            <div>
              <label className="eyebrow eyebrow-blue block mb-2">Phone</label>
              <input data-testid="waitlist-phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3" placeholder="+1 555 555 5555" required />
              <p className="mt-2 text-[10px] text-zinc-500 font-mono-eyebrow uppercase tracking-[0.18em]">Standard message rates apply.</p>
            </div>
          )}
          <button data-testid="waitlist-submit" type="submit" disabled={saving} className="w-full rc-btn-primary disabled:opacity-50">{saving ? "Saving…" : "Add me to the list"}</button>
        </form>
      </div>
    </div>
  );
}
